import { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import { X } from '@phosphor-icons/react';
import { cn } from '../../utils/cn';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ModalProps {
  /** Controls visibility */
  isOpen: boolean;
  /** Called when user dismisses (ESC, backdrop click, close button) */
  onClose: () => void;
  /** Modal title displayed in header (also used for aria-label) */
  title: string;
  children: React.ReactNode;
  /** Width constraint on desktop (full-screen on mobile regardless) */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Remove default body padding */
  noPadding?: boolean;
  className?: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const sizeStyles: Record<string, string> = {
  sm: 'lg:max-w-[400px]',
  md: 'lg:max-w-[520px]',
  lg: 'lg:max-w-[680px]',
  xl: 'lg:max-w-[860px]',
};

const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  noPadding = false,
  className,
}: ModalProps) {
  const intl = useIntl();
  const overlayRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // --------------------------------------------------
  // Focus management: capture + restore + auto-focus
  // --------------------------------------------------
  useEffect(() => {
    if (!isOpen) return;

    previousFocusRef.current = document.activeElement as HTMLElement;

    // Delay focus slightly so animation begins before focus shifts
    const timer = setTimeout(() => {
      const firstFocusable =
        contentRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      firstFocusable?.focus();
    }, 50);

    return () => {
      clearTimeout(timer);
      // Restore focus to the element that opened the modal
      previousFocusRef.current?.focus();
    };
  }, [isOpen]);

  // --------------------------------------------------
  // ESC key + focus trap + body scroll lock
  // --------------------------------------------------
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }

      // Focus trap: Tab cycling within the modal content
      if (e.key === 'Tab' && contentRef.current) {
        const focusables =
          contentRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
        if (focusables.length === 0) return;

        const first = focusables[0];
        const last = focusables[focusables.length - 1];

        if (first && e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        } else if (last && !e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    // Prevent body scroll while modal is open
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // --------------------------------------------------
  // Backdrop click handler
  // --------------------------------------------------
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === overlayRef.current) onClose();
    },
    [onClose],
  );

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={overlayRef}
      className={cn(
        'fixed inset-0 z-[var(--z-modal)] flex items-center justify-center',
        'bg-bg-overlay backdrop-blur-[6px]',
        'animate-[fade-in_200ms_ease-out]',
      )}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        ref={contentRef}
        className={cn(
          // Full-screen on mobile, card on desktop
          'w-full h-full max-w-full max-h-full rounded-none bg-bg-elevated',
          'border border-border-subtle shadow-xl',
          'animate-modal-in',
          'lg:h-auto lg:max-h-[90vh] lg:rounded-xl lg:mx-4',
          sizeStyles[size],
          className,
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-4 lg:px-6 border-b border-border-subtle">
          <h2 className="min-w-0 break-words text-lg font-semibold text-text-primary pr-4">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className={cn(
              'shrink-0 text-text-tertiary hover:text-text-primary transition-colors cursor-pointer',
              'min-w-[44px] min-h-[44px] flex items-center justify-center lg:min-w-0 lg:min-h-0',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus rounded',
            )}
            aria-label={intl.formatMessage({ id: 'a11y.closeModal' })}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div
          className={cn(
            'max-h-[calc(100dvh-64px)] lg:max-h-[80vh] overflow-y-auto',
            noPadding ? '' : 'px-4 py-4 lg:px-6',
          )}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}
